import type { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import type { OnboardingWizardState } from './state';
import { initialState } from './state';
import type { OnboardingWizardCommand } from './commands';

type Extract_<T extends OnboardingWizardCommand['type']> =
  Extract<OnboardingWizardCommand, { type: T }>;

const handleOpen = ( state: OnboardingWizardState ): OnboardingWizardState => ({
  ...state,
  open: true
});

/**
 * Closing keeps everything. The connected channel and the running import stay in
 * state (and, more importantly, in the conversation) so re-opening — or the
 * floating assistant — shows the same thing rather than a blank first screen.
 */
const handleClose = ( state: OnboardingWizardState ): OnboardingWizardState => ({
  ...state,
  open: false,
  connectPanelOpen: false
});

const handleSetStep = (
  state: OnboardingWizardState,
  cmd: Extract_<'onboardingWizard/ui/setStep'>
): OnboardingWizardState => ({
  ...state,
  step: cmd.payload.step
});

const handleSelect = (
  state: OnboardingWizardState,
  cmd: Extract_<'onboardingWizard/channel/select'>
): OnboardingWizardState => ({
  ...state,
  selectedConnectorId: cmd.payload.connectorId,
  // A different choice invalidates whatever the previous panel collected.
  connectPanelOpen: false,
  error: null
});

const handleOpenConnectPanel = ( state: OnboardingWizardState ): OnboardingWizardState => ({
  ...state,
  connectPanelOpen: true,
  error: null
});

const handleCloseConnectPanel = ( state: OnboardingWizardState ): OnboardingWizardState => ({
  ...state,
  connectPanelOpen: false
});

const handleConnectStarted = ( state: OnboardingWizardState ): OnboardingWizardState => ({
  ...state,
  connectionWait: 'waiting',
  error: null
});

const handleConnectDetected = (
  state: OnboardingWizardState,
  cmd: Extract_<'onboardingWizard/connect/detected'>
): OnboardingWizardState => ({
  ...state,
  connectionWait: 'idle',
  connectionOrigin: 'wizard',
  connectedIntegrationId: cmd.payload.integrationId,
  connectedConnectorId: cmd.payload.connectorId,
  connectedIntegrationUserId: cmd.payload.integrationUserId,
  connectedLabel: cmd.payload.label,
  connectPanelOpen: false,
  error: null,
  // Advancing here, not in the component: the screen change IS the consequence
  // of a channel existing, and putting it in an onClick would let the two get
  // out of step whenever a connection is detected some other way.
  step: 'nextSteps'
});

/**
 * Same landing place as `detected`, different origin — and the origin is what
 * the policy reads to decide whether to start an import. Idempotent on purpose:
 * a re-delivered snapshot must not restart the flow for a channel already
 * recorded.
 */
const handlePreexisting = (
  state: OnboardingWizardState,
  cmd: Extract_<'onboardingWizard/connect/preexisting'>
): OnboardingWizardState =>
  state.connectedIntegrationId === cmd.payload.integrationId
    ? state
    : {
      ...state,
      connectionWait: 'idle',
      connectionOrigin: 'preexisting',
      connectedIntegrationId: cmd.payload.integrationId,
      connectedConnectorId: cmd.payload.connectorId,
      connectedIntegrationUserId: cmd.payload.integrationUserId,
      connectedLabel: cmd.payload.label,
      connectPanelOpen: false,
      error: null,
      step: 'nextSteps'
    };

const handleTimedOut = ( state: OnboardingWizardState ): OnboardingWizardState =>
  // A connection that landed while the timer was running wins over the timer.
  state.connectedIntegrationId
    ? state
    : { ...state, connectionWait: 'timedOut' };

/**
 * Back to a clean slate for THIS attempt: the wait stops and the selection
 * survives, so the obvious next move (fix the store URL, press connect again)
 * needs no re-picking. A connection that already landed is not cancellable —
 * the channel exists.
 */
const handleCancelWait = ( state: OnboardingWizardState ): OnboardingWizardState =>
  state.connectedIntegrationId
    ? state
    : { ...state, connectionWait: 'idle', error: null };

const handleRetryWait = ( state: OnboardingWizardState ): OnboardingWizardState => ({
  ...state,
  connectionWait: 'waiting'
});

const handleConnectFailed = (
  state: OnboardingWizardState,
  cmd: Extract_<'onboardingWizard/connect/failed'>
): OnboardingWizardState => ({
  ...state,
  connectionWait: 'idle',
  error: cmd.payload.message
});

const handleImportStarted = (
  state: OnboardingWizardState,
  cmd: Extract_<'onboardingWizard/import/started'>
): OnboardingWizardState => ({
  ...state,
  importBatchProcessId: cmd.payload.batchProcessId,
  // A run WAS attached, so whatever earlier verdict said otherwise is stale.
  importUnavailable: false
});

const handleImportUnavailable = ( state: OnboardingWizardState ): OnboardingWizardState => ({
  ...state,
  importUnavailable: true
});

/**
 * The orders trio. Separate fields, separate transitions — an order seeding the
 * connector rejects must not disturb a publications run that is going fine.
 */
const handleOrderImportRequest = ( state: OnboardingWizardState ): OnboardingWizardState => ({
  ...state,
  orderImportRequested: true,
  orderImportUnavailable: false
});

const handleOrderImportStarted = (
  state: OnboardingWizardState,
  cmd: Extract_<'onboardingWizard/orderImport/started'>
): OnboardingWizardState => ({
  ...state,
  orderImportBatchProcessId: cmd.payload.batchProcessId,
  orderImportUnavailable: false
});

const handleOrderImportUnavailable = ( state: OnboardingWizardState ): OnboardingWizardState => ({
  ...state,
  orderImportUnavailable: true
});

const handleSnapshotReady = ( state: OnboardingWizardState ): OnboardingWizardState =>
  state.integrationsSnapshotReady ? state : { ...state, integrationsSnapshotReady: true };

const handleClearError = ( state: OnboardingWizardState ): OnboardingWizardState => ({
  ...state,
  error: null
});

export const handlers: CommandHandlerMap<OnboardingWizardState, OnboardingWizardCommand> = {
  'onboardingWizard/ui/open': handleOpen,
  'onboardingWizard/ui/close': handleClose,
  'onboardingWizard/ui/setStep': handleSetStep,
  'onboardingWizard/channel/select': handleSelect,
  'onboardingWizard/channel/openConnectPanel': handleOpenConnectPanel,
  'onboardingWizard/channel/closeConnectPanel': handleCloseConnectPanel,
  'onboardingWizard/connect/started': handleConnectStarted,
  'onboardingWizard/connect/detected': handleConnectDetected,
  'onboardingWizard/connect/preexisting': handlePreexisting,
  'onboardingWizard/connect/timedOut': handleTimedOut,
  'onboardingWizard/connect/retryWait': handleRetryWait,
  'onboardingWizard/connect/cancelWait': handleCancelWait,
  'onboardingWizard/connect/failed': handleConnectFailed,
  'onboardingWizard/import/started': handleImportStarted,
  'onboardingWizard/import/unavailable': handleImportUnavailable,
  'onboardingWizard/orderImport/request': handleOrderImportRequest,
  'onboardingWizard/orderImport/started': handleOrderImportStarted,
  'onboardingWizard/orderImport/unavailable': handleOrderImportUnavailable,
  'onboardingWizard/ui/clearError': handleClearError,
  'onboardingWizard/ui/snapshotReady': handleSnapshotReady
};

export { initialState };
